import React from "react";
import { Card, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import moment from "moment";
import warnIcon from "../../assets/images/warning_sign_gradient_shine.jpg";
import { convertCurrencyToSignNew, htmlDecode } from "../../utils/Constant";

const currnencyText = {
  gbp: "UK Pounds",
  eur: "Euro",
};

function RequestCard({
  bill,
  handlePage,
  btn_title,
  signButton,
  isBankPayment,
  showChargeAbleAmount = false,
  isDetailShow = false,
}) {
  const navigate = useNavigate();

  const handleNavigate = () => {
    if (
      bill.agreement_config?.is_sign_agreement &&
      !bill?.is_agreement_signed
    ) {
      navigate(`/agreement/${bill.payment_request_slug}`);
    }
  };

  const replacedString = (string) => {
    if (string) {
      string = string.replace(
        /{exchange_rate}/g,
        `<span class="text-golden">${bill?.cost_in_one_pound}</span>`
      );
      string = string.replace(
        /{to_currency}/g,
        currnencyText[bill.converted_currency.toLowerCase()] +
          " (" +
          bill.converted_currency.toUpperCase() +
          ")"
      );
      return string;
    }
  };

  function calculateChargeableAmount(name = "initial_amount") {
    let baseAmount = 0;

    if (bill.request_type === "onetime") {
      baseAmount = bill.total_amount;
    } else if (bill.request_type === "recurring") {
      baseAmount = bill[name];
    }

    if (baseAmount <= 0) return 0;

    const vatAmount =
      bill.vat_percentage > 0 ? (baseAmount * bill.vat_percentage) / 100 : 0;

    const chargeableAmount = baseAmount + vatAmount;

    return parseFloat(chargeableAmount.toFixed(2));
  }

  return (
    <Card
      className="payment-card mb-3 modern-request-card"
      style={{
        borderRadius: 16,
        boxShadow: "0 2px 12px 0 rgba(0,0,0,0.06)",
        border: "1px solid #ececec",
        padding: 0,
        background: "#fff",
        overflow: "hidden",
        width: "100%",
        maxWidth: "100%",
        margin: 0,
      }}
    >
      {/* Card Header */}
      <div
        style={{
          background: "#f7f7f7",
          color: "#222",
          padding: "1.25rem 1.5rem 1rem 1.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #ececec",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Icon
            icon="mdi:cash-multiple"
            width={28}
            height={28}
            color="#F5CE6E"
          />
          <div style={{ fontWeight: 600, fontSize: 19 }}>
            {htmlDecode(bill.request_title)}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Icon icon="uiw:date" width={20} height={20} color="#F5CE6E" />
          <span
            style={{
              color: "#222",
              fontWeight: 600,
              fontSize: 15,
              letterSpacing: 0.1,
            }}
          >
            {moment(bill.createdAt).format("MMM DD, YYYY")}
          </span>
        </div>

        {signButton &&
          bill.agreement_config &&
          bill.agreement_config.is_sign_agreement === true &&
          bill?.is_agreement_signed === false && (
            <Tooltip title="Please sign your agreement by clicking here">
              <img
                src={warnIcon}
                className="warning-icons"
                onClick={handleNavigate}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  cursor: "pointer",
                  border: "2px solid #fff",
                }}
                alt="Sign Agreement"
              />
            </Tooltip>
          )}
      </div>

      {/* Card Body */}
      <div className="p-4">
        <div className="row g-3">
          {/* Request Type */}
          <div className="col-lg-6">
            <div className="d-flex align-items-center gap-3 bg-light rounded p-3">
              <Icon
                icon="mdi:tag-outline"
                width={22}
                height={22}
                color="#F5CE6E"
              />
              <div className="flex-grow-1">
                <div
                  className="fw-semibold text-secondary"
                  style={{ fontSize: 15 }}
                >
                  Request Type
                </div>
                <div className="fw-bold mt-1" style={{ fontSize: 16 }}>
                  {bill.request_type === "onetime" ? "OneTime" : "Recurring"}
                </div>
              </div>
            </div>
          </div>

          {/* Amount (Onetime) */}
          {bill.request_type === "onetime" && (
            <div className="col-lg-6">
              <div className="d-flex align-items-center gap-3 bg-light rounded p-3">
                <Icon
                  icon="mdi:currency-usd"
                  width={22}
                  height={22}
                  color="#F5CE6E"
                />
                <div className="flex-grow-1">
                  <div
                    className="fw-semibold text-secondary"
                    style={{ fontSize: 15 }}
                  >
                    Amount
                  </div>
                  <div className="fw-bold mt-1" style={{ fontSize: 16 }}>
                    {convertCurrencyToSignNew(bill.currency)}
                    {bill.total_amount.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recurring: Initial Deposit & Installments */}
          {bill.request_type === "recurring" && (
            <>
              <div className="col-lg-6">
                <div className="d-flex align-items-center gap-3 bg-light rounded p-3">
                  <Icon
                    icon="mdi:wallet-plus-outline"
                    width={22}
                    height={22}
                    color="#F5CE6E"
                  />
                  <div className="flex-grow-1">
                    <div
                      className="fw-semibold text-secondary"
                      style={{ fontSize: 15 }}
                    >
                      Initial Deposit
                    </div>
                    <div className="fw-bold mt-1" style={{ fontSize: 16 }}>
                      {convertCurrencyToSignNew(bill.currency)}
                      {bill.initial_amount.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="d-flex align-items-center gap-3 bg-light rounded p-3">
                  <Icon
                    icon="mdi:calendar-repeat"
                    width={22}
                    height={22}
                    color="#F5CE6E"
                  />
                  <div className="flex-grow-1">
                    <div
                      className="fw-semibold text-secondary"
                      style={{ fontSize: 15 }}
                    >
                      {bill.payment_type == "monthly"
                        ? "Monthly Installments"
                        : "Yearly Installment"}
                    </div>
                    <div className="fw-bold mt-1" style={{ fontSize: 16 }}>
                      {convertCurrencyToSignNew(bill.currency)}
                      {bill.installment_amount.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* VAT */}

          <div className="col-lg-6">
            <div className="d-flex align-items-center gap-3 bg-light rounded p-3">
              <Icon
                icon="mdi:percent-outline"
                width={22}
                height={22}
                color="#F5CE6E"
              />
              <div className="flex-grow-1">
                <div
                  className="fw-semibold text-secondary"
                  style={{ fontSize: 15 }}
                >
                  VAT
                </div>
                <div className="fw-bold mt-1" style={{ fontSize: 16 }}>
                  {bill.vat_percentage ? bill.vat_percentage : "0"}%
                </div>
              </div>
            </div>
          </div>

          {/* Extra Detail Section */}
          {isDetailShow && (
            <>
              {/* Chargeable Amount */}
              {bill.request_type === "onetime" && (
                <div className="col-lg-6">
                  <div className="d-flex align-items-center gap-3 bg-light rounded p-3">
                    <Icon
                      icon="mdi:credit-card-outline"
                      width={22}
                      height={22}
                      color="#F5CE6E"
                    />
                    <div className="flex-grow-1">
                      <div
                        className="fw-semibold text-secondary"
                        style={{ fontSize: 15 }}
                      >
                        Chargeable Amount
                      </div>
                      <div className="fw-bold mt-1" style={{ fontSize: 16 }}>
                        {convertCurrencyToSignNew(bill.currency)}
                        {calculateChargeableAmount("total_amount")}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Chargeable Initial Amount */}
              {bill.request_type === "recurring" && (
                <div className="col-lg-6">
                  <div className="d-flex align-items-center gap-3 bg-light rounded p-3">
                    <Icon
                      icon="mdi:wallet-outline"
                      width={22}
                      height={22}
                      color="#F5CE6E"
                    />
                    <div className="flex-grow-1">
                      <div
                        className="fw-semibold text-secondary"
                        style={{ fontSize: 15 }}
                      >
                        Chargeable Initial Amount
                      </div>
                      <div className="fw-bold mt-1" style={{ fontSize: 16 }}>
                        {convertCurrencyToSignNew(bill.currency)}
                        {calculateChargeableAmount("initial_amount")}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Chargeable Installment */}
              {bill.request_type === "recurring" && (
                <div className="col-lg-6">
                  <div className="d-flex align-items-center gap-3 bg-light rounded p-3">
                    <Icon
                      icon="mdi:calendar-month-outline"
                      width={22}
                      height={22}
                      color="#F5CE6E"
                    />

                    <div className="flex-grow-1">
                      <div
                        className="fw-semibold text-secondary"
                        style={{ fontSize: 15 }}
                      >
                        Chargeable Installment
                      </div>
                      <div className="fw-bold mt-1" style={{ fontSize: 16 }}>
                        {convertCurrencyToSignNew(bill.currency)}
                        {calculateChargeableAmount("installment_amount")}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {bill.transaction_note && isDetailShow && (
            <div className="">
              <div className="d-flex align-items-start gap-3 bg-light rounded p-3">
                <div
                  style={{
                    width: "22px",
                  }}
                  className="mt-3"
                >
                  <Icon
                    icon="mdi:note-text-outline"
                    width={22}
                    height={22}
                    color="#F5CE6E"
                  />
                </div>
                <div className="flex-grow-1">
                  <div
                    className="fw-semibold text-secondary"
                    style={{ fontSize: 15 }}
                  >
                    Payment Notes
                  </div>
                  <div
                    className="fw-bold mt-1"
                    style={{ fontSize: 16, whiteSpace: "pre-line" }}
                  >
                    {htmlDecode(bill.transaction_note)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Normal Chargeable Amount Footer */}
        {showChargeAbleAmount && (
          <div className="mt-4 p-3 d-flex align-items-center justify-content-between bg-light rounded shadow-sm">
            <div className="d-flex align-items-center gap-2">
              <Icon
                icon="mdi:credit-card-outline"
                width={22}
                height={22}
                color="#F5CE6E"
              />
              <span className="fw-bold" style={{ fontSize: 16 }}>
                {bill.payment_request_type !== "pending"
                  ? "Charged Amount"
                  : "Chargeable Amount"}
                :
              </span>
            </div>
            <div className="fw-bold" style={{ fontSize: 20, color: "navy" }}>
              {convertCurrencyToSignNew(bill.currency)}
              {calculateChargeableAmount()}
            </div>
          </div>
        )}

        {/* Button */}
        {handlePage && (
          <div className="text-center mt-4">
            <button
              className="btn btn-primary fw-bold px-4 py-2"
              style={{ background: "#000080", border: "none" }}
              onClick={() => handlePage(bill)}
            >
              {btn_title}
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}

export default RequestCard;
